import {Button, Divider, Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {CopySection, StyledCard, TagChip} from 'presentation/components';
import {translations as newTranslations, translations} from 'presentation/translations-new';
import {formatDateString, formatPriceBill, formatText} from 'presentation/utils';

import AlertErrorCodeBar from 'presentation/components/AlertErrorCodeBar';
import ContactPhones from 'presentation/components/ContactPhones';
import {
	BillStatusEnum,
	ChatRoomsEnum,
	CopySectionMaskEnum,
	MyPaymentsEnum,
} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useCallChatRoom} from 'presentation/pages/private/hooks';
import {useSelectedBilletStore} from 'presentation/pages/private/my-payments-flow/store/selectedBilletStore';
import {BillsTaggingEnum} from 'main/analytics/gaTagsEnum';
import {FinancialCardProps, StatusConfigProps} from './types';

const {
	inNegotiationAlert,
	inNegotiationPhone,
	copiedCodeMessage,
	interestFines,
	billStatus,
	dueDate,
	paymentCode,
} = translations['pt-br'].privateFlow.myFinancial;
const {speakToOnlineSupport, showDetails} = newTranslations['pt-br'].commons;

export function FinancialCard({
	bill,
	copyDataTagId,
	callDataTagId,
	detailsDataTagId,
	onCheckOlderBillsOnDetails,
	onCheckOlderBillsOnCopy,
	copyTestId,
	detailsTestId,
	inNegotiationAlertText,
}: FinancialCardProps) {
	const {dataHostNameState} = useHostName();
	const {callChatRoom} = useCallChatRoom();
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {colors, palette} = useTheme();

	const setSelectedCurrentBillet = useSelectedBilletStore(state => state.setSelectedBillet);

	const {dtPrevista, status, linhaDigitavel, flTipoPdf, flMigracao} = bill;

	if (!linhaDigitavel) {
		return null;
	}

	const {vlObrigacao, dtVencimento, cdBarra} = linhaDigitavel;

	const formattedTargetDate = formatDateString(dtPrevista, 'dd/MM/yyyy', 'MMMM/yyyy');
	const formattedPrice = vlObrigacao ? formatPriceBill(vlObrigacao).split('\u00A0') : ['R$', '-'];
	const isInNegotiation = status === BillStatusEnum.IN_NEGOTIATION;
	const isOpenBill = status === BillStatusEnum.OPEN;
	const showBarCodeButton = !!cdBarra && flTipoPdf && !flMigracao;
	const showAlertErrorCodeBar = !cdBarra && !flTipoPdf;

	const statusConfig: Record<BillStatusEnum, StatusConfigProps> = {
		[BillStatusEnum.OPEN]: {
			chipType: 'info',
			color: palette.primary.main,
			chipText: billStatus.open,
		},
		[BillStatusEnum.EXPIRED]: {
			chipType: 'warning',
			color: palette.secondary.main,
			chipText: billStatus.expired,
		},
		[BillStatusEnum.IN_NEGOTIATION]: {
			chipType: 'error',
			color: palette.error.main,
			chipText: billStatus.inNegotiation,
		},
		[BillStatusEnum.PAID]: {
			chipType: 'success',
			color: palette.success.main,
			chipText: billStatus.paid,
		},
	};

	const contactPhones = inNegotiationPhone[dataHostNameState.product];

	const {chipType, color, chipText} = statusConfig[status];

	const handleGoToDetails = () => {
		if (onCheckOlderBillsOnDetails) {
			const hasOlderBills = onCheckOlderBillsOnDetails();
			if (hasOlderBills) return;
		}
		setSelectedCurrentBillet(bill);
		navigate(MyPaymentsEnum.BILLET_DETAILS, {state: pathname});
	};

	return (
		<StyledCard.Container $borderTop $borderColor={color} elevation={0}>
			<Stack gap={'16px'}>
				<Stack justifyContent="space-between" alignItems="center" direction="row">
					<Typography color={palette.text.primary} variant="h6">
						{formatText(formattedTargetDate)}
					</Typography>

					<TagChip label={chipText} color={chipType} />
				</Stack>
				<Divider />
				<Stack gap={'8px'}>
					<Typography variant="body2" color={palette.text.secondary}>
						{dueDate}{' '}
						<Typography variant="body2" fontWeight={700} component="span">
							{dtVencimento || '-'}
						</Typography>
					</Typography>
					<Stack alignItems="center" color="text.primary" direction="row" gap={0.5}>
						<Typography>{formattedPrice[0]}</Typography>
						<Typography variant="h5" fontWeight={700}>
							{formattedPrice[1]}
						</Typography>
						{!isOpenBill ? <Typography>{interestFines}</Typography> : null}
					</Stack>
				</Stack>
				{isInNegotiation ? (
					<Fragment>
						<Typography variant="body2" color="text.primary" lineHeight={'1.25rem'}>
							{inNegotiationAlertText ??
								inNegotiationAlert[dataHostNameState.product]}
						</Typography>
						<ContactPhones
							copyDataTagId={
								BillsTaggingEnum.CLICK_COPIAR_TELEFONE_ATENDIMENTO_BOLETO
							}
							phones={contactPhones}
						/>
						<Button
							data-tag-id={callDataTagId}
							variant="outlined"
							onClick={() => callChatRoom(ChatRoomsEnum.DEBIT_NEGOTIATION)}>
							{speakToOnlineSupport}
						</Button>
					</Fragment>
				) : (
					<Fragment>
						{showBarCodeButton ? (
							<Stack direction="column" alignItems={'start'} gap={1} flexWrap="wrap">
								<Typography
									fontSize={'0.75rem'}
									color={colors.light.text.secondary}>
									{paymentCode}
								</Typography>
								<CopySection
									dataTagId={{copy: copyDataTagId}}
									value={cdBarra}
									highlightedIcons
									mask={CopySectionMaskEnum.WITHOUT_MASK}
									snackbarMessage={copiedCodeMessage}
									customPadding="8px 16px"
									onBeforeCopy={onCheckOlderBillsOnCopy}
									testId={copyTestId}
								/>
							</Stack>
						) : null}
						{showAlertErrorCodeBar ? <AlertErrorCodeBar /> : null}
					</Fragment>
				)}
			</Stack>
			<Stack alignItems={'end'}>
				<Button
					variant="text"
					onClick={handleGoToDetails}
					data-tag-id={detailsDataTagId ?? BillsTaggingEnum.CLICK_VER_DETALHES_BOLETO}
					data-testid={detailsTestId}>
					{showDetails}
				</Button>
			</Stack>
		</StyledCard.Container>
	);
}
